import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    ApartmentOutlined,
    BarChartOutlined,
    InboxOutlined,
    LineChartOutlined,
    UserOutlined,
    MonitorOutlined,
    ClockCircleOutlined,
    CarryOutOutlined,
    HistoryOutlined,
    DownloadOutlined,
    CreditCardOutlined,
    ExclamationCircleOutlined,
    EnvironmentOutlined,
    SolutionOutlined,
} from '@ant-design/icons';
import MainLayout from '../../../layouts/MainLayout';
import Loading from 'components/Loading';
import { ErrorInfo } from 'components/ErrorInfo';
import WarehouseSelector, { WarehouseDisplayMode } from '../../../components/WarehouseSelector';
import { useGetAllAccessWarehousesForRole } from '../../../hooks/useWarehouseQuery';
import { t } from '../../../i18n';
import {isDspManager, isDspOwnerTeam} from "../../../utils/auth";
import {WarehouseProvider} from "../../../contexts/WarehouseContext";
import { findFirstAvailableMenuItem } from '../../../utils/menuUtils';
import { useCheckWarehouseGrayConfig } from '../../../graphql/queries/checkWarehouseGrayConfig';
import { useCheckPenaltyDspImportGray } from '../../../graphql/queries/checkPenaltyDspImportGray';
import { useUnassignedDriverCount } from '../../../hooks/useUnassignedDriverCount';

/** DSP 问题件工作台入口按仓灰度配置键（与后端 ConfigKey.DSP_ISSUE_PARCEL_WORKBENCH_GRAY_CONFIG 一致） */
const ISSUE_PARCEL_WORKBENCH_GRAY_CONFIG_KEY = 'dsp.issue_parcel_workbench.gray_config';
const POD_EXEMPT_GRAY_CONFIG_KEY = 'pod_exempt.gray_config';

const DeliveryLayout: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // 获取warehouse数据
    const { warehouses, loading, error } = useGetAllAccessWarehousesForRole({
        fetchPolicy: 'cache-first'
    });

    const [currentWarehouse, setCurrentWarehouse] = useState<any>(null);
    const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>('');

    // 处理仓库选择变化
    const handleWarehouseChange = useCallback((warehouseId: string, siteType: number) => {
        console.log('🚚 DeliveryLayout仓库选择:', { warehouseId, siteType });
        setSelectedWarehouseId(warehouseId);

        const selectedWarehouse = warehouses.find(w => w.id?.toString() === warehouseId);
        if (selectedWarehouse) {
            setCurrentWarehouse(selectedWarehouse);
        }
    }, [warehouses]);

    // 自动选择第一个warehouse
    useEffect(() => {
        if (warehouses.length > 0 && !currentWarehouse) {
            const firstWarehouse = warehouses[0];
            console.log('🚚 自动选择第一个仓库:', { id: firstWarehouse.id, siteType: firstWarehouse.siteType });
            setCurrentWarehouse(firstWarehouse);
            setSelectedWarehouseId(firstWarehouse.id?.toString() || '');
        }
    }, [warehouses, currentWarehouse]);
    
    // 问题件工作台入口按当前选中仓灰度：命中才展示菜单入口，未命中不展示（参考 exceptionOrder 灰度实现）
    const { isInGrayConfig: isIssueWorkbenchGray } = useCheckWarehouseGrayConfig(
        ISSUE_PARCEL_WORKBENCH_GRAY_CONFIG_KEY,
        selectedWarehouseId || undefined,
        !selectedWarehouseId,
    );

    // 门牌免拍入口按当前选中仓灰度（与后端 pod_exempt.gray_config 同 key）
    const { isInGrayConfig: isPodExemptGray } = useCheckWarehouseGrayConfig(
        POD_EXEMPT_GRAY_CONFIG_KEY,
        selectedWarehouseId || undefined,
        !selectedWarehouseId,
    );

    // 罚单管理中心灰度：与罚单导入共用 partner_penalty.dsp_import_whitelist（key 在后端固定）。
    // 这里刻意不传 selectedWarehouseId：本页的仓库选择器是给配送业务用的，罚单灰度必须只看登录 DSP 自己。
    // 传仓会走后端「该仓关联 DSP 任一命中」分支，同仓其它 DSP 灰度了会把未灰度的 DSP 一起放出来
    const { isInGrayConfig: isPenaltyCenterGray } = useCheckPenaltyDspImportGray({ byLoginDsp: true });

    const unassignedCount = useUnassignedDriverCount(
        selectedWarehouseId ? Number(selectedWarehouseId) : undefined,
        'dsp',
    );

    const menuItems = useMemo(() => [
        {
            key: 'delivery-progress',
            label: t('delivery.menu.progress'),
            icon: <LineChartOutlined />,
            display: isDspManager() || isDspOwnerTeam(),
            children: [
                {
                    key: 'daily-task-monitoring',
                    label: t('wms.delivery.dailyTaskMonitoring.title'),
                    icon: <MonitorOutlined />,
                    display: isDspManager() || isDspOwnerTeam(),
                    onClick: () => navigate('/delivery/daily-task-monitoring')
                },
                {
                    key: 'parcel-sla-monitoring',
                    label: t('wms.delivery.parcelSlaMonitoring.menuTitle'),
                    icon: <ClockCircleOutlined />,
                    display: isDspManager() || isDspOwnerTeam(),
                    onClick: () => navigate('/delivery/parcel-sla-monitoring')
                }
            ]
        },
        {
            key: 'delivery-dashboard',
            label: t('wms.dashboard.title'),
            icon: <LineChartOutlined />,
            display: isDspManager() || isDspOwnerTeam(),
            children: [
                {
                    key: 'dispatch-delivery-summary',
                    label: t('wms.dashboard.dspDeliveryManagement'),
                    display: isDspManager() || isDspOwnerTeam(),
                    onClick: () => navigate('/delivery/dispatch-delivery-summary')
                }
                ,
                {
                    key: 'delivery-completed-summary',
                    label: t('wms.delivery.deliveryCompletedSummary.shortMenuTitle'),
                    display: isDspManager() || isDspOwnerTeam(),
                    onClick: () => navigate('/delivery/delivery-completed-summary')
                }
            ]
        },
        {
            key: 'reports',
            label: t('delivery.menu.reports'),
            icon: <BarChartOutlined />,
            display: false,
            onClick: () => navigate('/delivery/reports')
        },
        {
            key: 'split-order',
            label: <>{t('delivery.menu.routes')}{unassignedCount > 0 && <span style={{marginLeft: 4, fontSize: 12, lineHeight: '18px', minWidth: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 9, backgroundColor: '#ff4d4f', color: '#fff', padding: '0 5px'}}>{unassignedCount}</span>}</>,
            icon: <ApartmentOutlined />,
            display: isDspManager() || isDspOwnerTeam(),
            onClick: () => navigate('/delivery/split-order')
        },
        {
            key: 'routes-history',
            label: t('delivery.menu.routesHistory'),
            icon: <HistoryOutlined />,
            display: isDspManager() || isDspOwnerTeam(),
            onClick: () => navigate('/delivery/routes-history')
        },
        {
            key: 'tasks',
            label: t('delivery.menu.tasks'),
            icon: <CarryOutOutlined />,
            display: true,
            onClick: () => navigate('/delivery/tasks')
        },
        {
            key: 'parcels',
            label: t('delivery.menu.parcels'),
            icon: <InboxOutlined />,
            display: isDspManager() || isDspOwnerTeam(),
            onClick: () => navigate('/delivery/parcels')
        },
        {
            key: 'issue-parcel-workbench',
            label: t('delivery.menu.issueParcelWorkbench'),
            icon: <ExclamationCircleOutlined />,
            display: (isDspManager() || isDspOwnerTeam()) && isIssueWorkbenchGray,
            onClick: () => navigate('/delivery/issue-parcel-workbench')
        },
        {
            // DSP 自助查看/申诉自己的罚单，与 /partner-control/penaltyCenter 复用同一页面组件
            key: 'penalty-center',
            label: t('partnerControl.menu.penaltyCenter'),
            icon: <SolutionOutlined />,
            display: (isDspManager() || isDspOwnerTeam()) && isPenaltyCenterGray,
            onClick: () => navigate('/delivery/penalty-center')
        },
        {
            key: 'accounts',
            label: t('delivery.menu.personnel'),
            icon: <UserOutlined />,
            display: isDspManager() || isDspOwnerTeam(),
            onClick: () => navigate('/delivery/accounts')
        },
        {
            key: 'download',
            label: t('delivery.menu.download'),
            icon: <DownloadOutlined />,
            display: isDspManager() || isDspOwnerTeam(),
            onClick: () => navigate('/delivery/download')
        },
        {
            key: 'dsp-bill',
            label: t('dspBill.title'),
            icon: <CreditCardOutlined />,
            display: isDspOwnerTeam(),
            onClick: () => navigate('/delivery/dsp-bill')
        },
        {
            key: 'finance-info',
            label: t('delivery.menu.financeInfo'),
            icon: <CreditCardOutlined />,
            display: isDspOwnerTeam(),
            onClick: () => navigate('/delivery/finance-info')
        },
        {
            key: 'pod-exempt',
            label: t('podExempt.menu.dsp'),
            icon: <EnvironmentOutlined />,
            display: (isDspManager() || isDspOwnerTeam()) && isPodExemptGray,
            onClick: () => navigate('/delivery/pod-exempt')
        }
    ], [navigate, t, isIssueWorkbenchGray,isPenaltyCenterGray,isPodExemptGray, unassignedCount]);

    useEffect(() => {
        const isRoot = location.pathname === '/delivery' || location.pathname === '/delivery/';
        if (!isRoot) return;
        const first = findFirstAvailableMenuItem(menuItems);
        if (first?.onClick) first.onClick();
    }, [location.pathname, menuItems]);

    // Header布局，包含仓库选择器
    const warehouseSelector = (
        <div className="flex items-center gap-4 px-4 py-2">
            <WarehouseSelector
                onWarehouseChange={handleWarehouseChange}
                displayMode={WarehouseDisplayMode.WAREHOUSE_ONLY}
                warehouses={warehouses}
                selectedWarehouseId={selectedWarehouseId}
            />
        </div>
    );

    return (
        <WarehouseProvider currentWarehouse={currentWarehouse}>
            <MainLayout
                activeFirstMenu="delivery"
                secondMenu={menuItems}
                secondHeader={warehouseSelector}
            >
                <div className="p-6 h-full flex flex-col">
                    {/* 只有在仓库数据加载完成 且 currentWarehouse 初始化完成后才渲染子组件 */}
                    {loading ? <Loading/> : (
                        error ? <ErrorInfo error={error}/> : (
                            currentWarehouse ? <Outlet/> : <Loading/>
                        )
                    )}
                </div>
            </MainLayout>
        </WarehouseProvider>
    );
};

export default DeliveryLayout; 